import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { Search, Edit, Trash2, X } from "lucide-react";
import { adminServices } from "../services/AdminServices";
import { toast } from "react-toastify";

// Set app element for react-modal
if (typeof window !== 'undefined' && document.getElementById('root')) {
  Modal.setAppElement('#root');
}

const MarkupSettingModal = ({ isOpen, onClose, onSuccess }) => {
  const [markupType, setMarkupType] = useState("global"); // "global" or "perFacility"
  const [globalMarkup, setGlobalMarkup] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [facilityMarkups, setFacilityMarkups] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editMarkup, setEditMarkup] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingFacilities, setLoadingFacilities] = useState(false);

  // Fetch current global markup when modal opens
  useEffect(() => {
    if (isOpen && markupType === "global") {
      adminServices.getData('MarkupSettings/global').then(res => {
        if (res?.data?.markup != null) {
          setGlobalMarkup(String(res.data.markup));
        }
      }).catch(() => { /* API may not support GET */ });
    }
  }, [isOpen, markupType]);

  // Fetch facilities from colonies when modal opens and perFacility is selected
  useEffect(() => {
    if (!isOpen) return;
    if (markupType === "perFacility" && facilityMarkups.length === 0) {
      fetchFacilities();
    }
  }, [isOpen, markupType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setGlobalMarkup("");
      setFacilityMarkups([]);
      setEditingId(null);
      setEditMarkup("");
    }
  }, [isOpen]);

  const fetchFacilities = async () => {
    setLoadingFacilities(true);
    try {
      const response = await adminServices.storeProcedure2('getColonies', {});
      if (response && response.data) {
        const facilities = [];
        let id = 1;
        (Array.isArray(response.data) ? response.data : []).forEach(colony => {
          const colonyFacilities = JSON.parse(colony.facilities || '[]');
          colonyFacilities.forEach(fac => {
            const facilityName = fac.Facility || fac.facilityName || String(fac.FacilityId || fac.facilityId || '');
            if (facilityName) {
              facilities.push({
                id: id++,
                nbp: colony.colony_name || "NBP",
                facility: facilityName,
                facilityId: fac.FacilityId || fac.facilityId,
                markup: "0"
              });
            }
          });
        });
        setFacilityMarkups(facilities);
      }
    } catch (error) {
      console.error("Error fetching facilities:", error);
      toast.error("Failed to load facilities");
    } finally {
      setLoadingFacilities(false);
    }
  };

  const handleEdit = (id, currentMarkup) => {
    setEditingId(id);
    setEditMarkup(currentMarkup);
  };

  const handleSaveEdit = (id) => {
    setFacilityMarkups(facilityMarkups.map(item => 
      item.id === id ? { ...item, markup: editMarkup } : item
    ));
    setEditingId(null);
    setEditMarkup("");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditMarkup("");
  };

  const handleDelete = (id) => {
    setFacilityMarkups(facilityMarkups.filter(item => item.id !== id));
  };

  const handleConfirm = async () => {
    if (markupType === "global") {
      const markupVal = parseFloat(globalMarkup);
      if (isNaN(markupVal) || markupVal < 0) {
        toast.error("Please enter a valid markup percentage");
        return;
      }
      setSaving(true);
      try {
        const response = await adminServices.updateGlobalMarkup(markupVal);
        if (response?.data?.success !== false) {
          toast.success(response?.data?.message || "Global markup updated successfully");
          onSuccess?.();
          onClose();
        } else {
          toast.error(response?.data?.message || "Failed to update global markup");
        }
      } catch (error) {
        console.error("Error updating global markup:", error);
        toast.error(error.response?.data?.message || "Failed to update global markup");
      } finally {
        setSaving(false);
      }
    } else {
      const facilitiesToSave = facilityMarkups.map(f => ({
        facility: f.facility,
        markup: (f.markup !== "" && !isNaN(parseFloat(f.markup))) ? parseFloat(f.markup) : 0
      }));
      if (facilitiesToSave.length === 0) {
        toast.error("No facilities to update. Please load facilities first.");
        return;
      }
      setSaving(true);
      try {
        const response = await adminServices.updateFacilityMarkups(facilitiesToSave);
        if (response?.data?.success !== false) {
          toast.success(response?.data?.message || "Facility markups updated successfully");
          onSuccess?.();
          onClose();
        } else {
          toast.error(response?.data?.message || "Failed to update facility markups");
        }
      } catch (error) {
        console.error("Error updating facility markups:", error);
        toast.error(error.response?.data?.message || "Failed to update facility markups");
      } finally {
        setSaving(false);
      }
    }
  };

  const filteredFacilities = facilityMarkups.filter(item =>
    item.facility.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.nbp.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="markup-setting-modal"
      overlayClassName="markup-setting-modal-overlay"
      ariaHideApp={false}
      shouldCloseOnOverlayClick={true}
    >
      <div className="markup-setting-modal-content">
        <div className="markup-setting-modal-header">
          <h2 className="markup-setting-modal-title">Markup Setting</h2>
          <button className="markup-setting-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="markup-setting-modal-body">
          {/* Set Markup (%) Section */}
          <div className="markup-setting-type-section">
            <label className="markup-setting-type-label">Set Markup (%)</label>
            <div className="markup-setting-radio-group">
              <label className="markup-setting-radio-label">
                <input
                  type="radio"
                  name="markupType"
                  value="global"
                  checked={markupType === "global"}
                  onChange={(e) => setMarkupType(e.target.value)}
                  className="markup-setting-radio-input"
                />
                <span className="markup-setting-radio-text">Global</span>
              </label>
              <label className="markup-setting-radio-label">
                <input
                  type="radio"
                  name="markupType"
                  value="perFacility"
                  checked={markupType === "perFacility"}
                  onChange={(e) => setMarkupType(e.target.value)}
                  className="markup-setting-radio-input"
                />
                <span className="markup-setting-radio-text">Per facility</span>
              </label>
            </div>
          </div>

          {/* Global Markup View */}
          {markupType === "global" && (
            <div className="markup-setting-global-section">
              <label className="markup-setting-input-label">Markup</label>
              <div className="markup-setting-input-wrapper">
                <input
                  type="text"
                  className="markup-setting-input"
                  placeholder="%"
                  value={globalMarkup}
                  onChange={(e) => setGlobalMarkup(e.target.value)}
                />
                <span className="markup-setting-percent-symbol">%</span>
              </div>
            </div>
          )}

          {/* Per Facility View */}
          {markupType === "perFacility" && (
            <div className="markup-setting-facility-section">
              {/* Search Bar */}
              <div className="markup-setting-search-container">
                <Search className="markup-setting-search-icon" size={18} />
                <input
                  type="text"
                  className="markup-setting-search-input"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  disabled={loadingFacilities}
                />
              </div>

              {/* Facility Markup List */}
              <div className="markup-setting-facility-list">
                {loadingFacilities ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
                    Loading facilities...
                  </div>
                ) : filteredFacilities.length === 0 ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
                    No facilities found
                  </div>
                ) : (
                filteredFacilities.map((item) => (
                  <div key={item.id} className="markup-setting-facility-row">
                    <input
                      type="text"
                      className="markup-setting-facility-nbp"
                      value={item.nbp}
                      readOnly
                    />
                    <select className="markup-setting-facility-dropdown">
                      <option>{item.facility}</option>
                    </select>
                    {editingId === item.id ? (
                      <div className="markup-setting-edit-group">
                        <input
                          type="text"
                          className="markup-setting-edit-input"
                          value={editMarkup}
                          onChange={(e) => setEditMarkup(e.target.value)}
                          onBlur={() => handleSaveEdit(item.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveEdit(item.id);
                            if (e.key === "Escape") handleCancelEdit();
                          }}
                          autoFocus
                        />
                        <span className="markup-setting-percent-symbol">%</span>
                      </div>
                    ) : (
                      <div className="markup-setting-markup-display">
                        <span>{item.markup} %</span>
                      </div>
                    )}
                    <button
                      className="markup-setting-action-btn markup-setting-edit-btn"
                      onClick={() => handleEdit(item.id, item.markup)}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className="markup-setting-action-btn markup-setting-delete-btn"
                      onClick={() => handleDelete(item.id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Buttons */}
        <div className="markup-setting-modal-footer">
          <button className="markup-setting-cancel-btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="markup-setting-confirm-btn"
            onClick={handleConfirm}
            disabled={saving || (markupType === "perFacility" && loadingFacilities)}
          >
            {saving ? "Saving..." : "Confirm"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default MarkupSettingModal;

